import { useRouter } from "@tanstack/react-router";
import { Button, Form } from "@patient-watch/ui";
import { Phone } from "@patient-watch/forms";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import z from "zod";
import { toast } from "@patient-watch/ui/sonner";
import { useSendOtp } from "../hooks/useSendOtp";
import { useCopy } from "../i18n/locale";

interface PhoneAuthProps {
    onSuccess: () => void;
}

const phoneSchema = (message: string) =>
    z.object({
        phone: z.string().min(1, message),
    });

type PhoneFormData = z.infer<ReturnType<typeof phoneSchema>>;

export function PhoneAuth({ onSuccess: _onSuccess }: PhoneAuthProps) {
    const router = useRouter();
    const sendOtpMutation = useSendOtp();
    const copy = useCopy();

    const form = useForm<PhoneFormData>({
        resolver: zodResolver(phoneSchema(copy.login.phoneRequired)),
        defaultValues: {
            phone: "",
        },
    });

    const handleSubmit = (data: PhoneFormData) => {
        sendOtpMutation.mutate(data.phone, {
            onSuccess: () => {
                toast.success(copy.login.codeSent, {
                    description: copy.login.codeSentDescription,
                });
                // Navigate to verify page with phone number
                router.navigate({
                    to: "/verify",
                    search: { phone: data.phone },
                });
            },
            onError: (error: any) => {
                toast.error(copy.login.sendFailed, {
                    description: error.message,
                });
            },
        });
    };

    return (
        <Form {...form}>
            <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
                <Phone
                    autoFocus={true}
                    name="phone"
                    className="[&>input]:border-slate-300 [&>input]:rounded-md [&>input]:h-12 [&>input]:text-lg"
                />
                {sendOtpMutation.error && <div className="text-red-600 text-sm">{sendOtpMutation.error.message}</div>}
                <Button type="submit" className="w-full" disabled={sendOtpMutation.isPending}>
                    {sendOtpMutation.isPending ? copy.common.sending : copy.login.sendCode}
                </Button>
            </form>
        </Form>
    );
}
